import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";

import { IAppUser, ICityTax, IGiveawayWithDetails, IInvoice, IVoucher } from "../../../interfaces";

import AppUsersService from "../../../services/http/services/AppUsersService";
import { EmitSuccessAlert } from "../../../utils/EmitSuccessAlert";
import { EmitError } from "../../../utils/EmitError";
import { onlyNumbers } from "../../../utils/Masks";
import InvoicesService, { IInvoicesTotal } from "../../../services/http/services/InvoicesService";
import useAuth from "../../../hooks/useAuth";
import { IMonthData } from "../interfaces";
import GiveawaysService from "../../../services/http/services/GiveawaysService";
import VouchersService from "../../../services/http/services/VouchersService";
import useModules from "../../../hooks/useModules";
import CityTaxesService from "../../../services/http/services/CityTaxesService";

export interface IStatsUserApp {
    total_tickets: number;
    total_raffle_with_tickets: number;
    total_invoices: number;
    value_total_invoices: number;
}

export default function useViewAppUser() {
    const { id } = useParams();
    const [user, setUser] = useState<IAppUser>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [totalProcessingInvoices, setTotalProcessingInvoices] = useState<number>(0);
    const [invoices, setInvoices] = useState<IInvoicesTotal[]>([]);
    const [invoicesInProcessing, setInvoicesInProcessing] = useState<IInvoice[]>([]);
    const [invoicesByMonth, setInvoicesByMonth] = useState<IInvoice[]>([]);
    const [giveaways, setGiveaways] = useState<Array<IGiveawayWithDetails>>([]);
    const [filterGiveaways, setFilterGiveaways] = useState<'all' | 'withTickets'>('withTickets');
    const [selectedGiveaway, setSelectedGiveaway] = useState<IGiveawayWithDetails>();
    const [showModalInvoice, setShowModalInvoice] = useState<boolean>(false);
    const [showModalInvoiceInProcessing, setShowModalInvoiceInProcessing] = useState<boolean>(false);
    const [showModalTickets, setShowModalTickets] = useState<boolean>(false);
    const [statsUser, setStatsUser] = useState<IStatsUserApp>({
        total_invoices: 0,
        total_raffle_with_tickets: 0,
        total_tickets: 0,
        value_total_invoices: 0
    });
    const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
    const [loadingInvoicesInProcessing, setLoadingInvoicesInProcessing] = useState<boolean>(false);
    const [loadingInvoicesReprocess, setLoadingInvoicesReprocess] = useState<boolean>(false);
    const [vouchers, setVoucher] = useState<IVoucher[]>();
    const { activeCity } = useAuth();
    const { hasRewardModule, hasTaxesModule } = useModules();
    const [taxes, setTaxes] = useState<ICityTax[]>();
    const [selectedYear, setSelectedYear] = useState<number | 'todos'>('todos');
    const controller = new AbortController();

    const navigate = useNavigate();

    const handleShowModalInvoice = useCallback(() => {
        setShowModalInvoice((prevState) => !prevState);
    }, []);

    const handleShowModalInvoiceInProcessing = useCallback(() => {
        setShowModalInvoiceInProcessing((prevState) => !prevState);
    }, []);

    const availableYears = useMemo(() => {
        const years = Array.from(
            new Set(
                giveaways.map(g => new Date(g.date).getFullYear())
            )
        );

        return ['todos', ...years].sort((a, b) => {
            if (a === 'todos') return -1;
            if (b === 'todos') return 1;

            return Number(b) - Number(a);
        });
    }, [giveaways]);

    const loadInvoicesInProcessing = useCallback(() => {

        async function loadData() {

            try {
                setLoadingInvoicesInProcessing(true);

                const invoicesList = await InvoicesService.listInvoicesInProcessing({
                    appUserId: Number(id),
                    cityId: activeCity?.id!,
                    signal: controller.signal,
                });


                setInvoicesInProcessing(invoicesList);
                setLoadingInvoicesInProcessing(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'viewAppUserInvoices_asodkaskldasd');
                setLoadingInvoices(false);
            }

        }

        loadData();

    }, [activeCity, controller.signal, id]);

    const handleClickViewInvoicesInProcessing = useCallback(() => {
        handleShowModalInvoiceInProcessing();
        loadInvoicesInProcessing();
    }, [handleShowModalInvoiceInProcessing, loadInvoicesInProcessing]);

    const handleSetFilterGiveaway = useCallback((filter: "all" | "withTickets") => {
        setFilterGiveaways(filter);
    }, []);


    const handleShowModalTickets = useCallback(() => {
        setShowModalTickets((prevState) => !prevState);
    }, []);

    const handleSetSelectedGiveaway = useCallback((giveaway: IGiveawayWithDetails) => {
        setSelectedGiveaway(giveaway);
    }, []);


    const loadInvoicesByPeriod = useCallback(({ month, year }: { month: number, year: number }) => {

        setInvoicesByMonth([]);
        async function loadData() {

            try {
                setLoadingInvoices(true);

                const invoicesList = await InvoicesService.listInvoicesByMonth({
                    appUserId: Number(id),
                    cityId: activeCity?.id!,
                    month: Number(month),
                    year: Number(year),
                    signal: controller.signal,
                });

                setInvoicesByMonth(invoicesList);
                setLoadingInvoices(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'viewAppUserInvoices_asodkaskldasd');
                setLoadingInvoices(false);
            }

        }

        loadData();

    }, [activeCity, controller.signal, id]);

    const reprocessInvoice = useCallback((invoiceId: number) => {

        async function sendInvoiceToReprocess() {

            try {
                setLoadingInvoicesReprocess(true);

                await InvoicesService.reprocessInvoice({
                    invoiceId,
                    appUserId: Number(id),
                    cityId: activeCity?.id!
                });

                loadInvoicesInProcessing();

                EmitSuccessAlert('Nota enviada para reprocessamento!');
                setLoadingInvoicesReprocess(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'viewAppUserInvoices_asodkaskldasd');
                setLoadingInvoicesReprocess(false);
            }

        }

        sendInvoiceToReprocess();

    }, [activeCity, loadInvoicesInProcessing, id]);


    const filteredGiveaways = useMemo(() => {
        let filtered = [...giveaways];

        if (selectedYear !== 'todos') {
            filtered = filtered.filter(
                giveaway =>
                    new Date(giveaway.date).getFullYear() === selectedYear
            );
        }

        if (filterGiveaways === 'withTickets') {
            filtered = filtered.filter(
                giveaway => giveaway.total_tickets > 0
            );
        }

        return filtered;
    }, [giveaways, filterGiveaways, selectedYear]);

    const filteredInvoices = useMemo(() => {
        if (selectedYear === 'todos') {
            return invoices;
        }

        return invoices.filter(
            invoice => invoice.year === selectedYear
        );
    }, [invoices, selectedYear]);

    useEffect(() => {
        const totalTickets = filteredGiveaways.reduce(
            (acc, giveaway) => acc + giveaway.total_tickets,
            0
        );

        const totalRaffleWithTickets = filteredGiveaways.filter(
            giveaway => giveaway.total_tickets > 0
        ).length;

        const totalInvoices = filteredInvoices.reduce(
            (acc, invoice) => acc + invoice.total_invoices,
            0
        );

        const valueTotalInvoices = filteredInvoices.reduce(
            (acc, invoice) => acc + invoice.total_value,
            0
        );

        setStatsUser({
            total_tickets: totalTickets,
            total_raffle_with_tickets: totalRaffleWithTickets,
            total_invoices: totalInvoices,
            value_total_invoices: valueTotalInvoices
        });
    }, [filteredGiveaways, filteredInvoices]);
    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                const appUserId = Number(id);
                const cityId = activeCity?.id!;
                const signal = controller.signal;

                const [invoicesList, giveawaysList, appUserData, voucherList, taxesList] = await Promise.all([
                    InvoicesService.listMonthsData({ appUserId, cityId, signal }),
                    GiveawaysService.listGiveawaysWithDetails({ cityId, appUserId, signal }),
                    AppUsersService.getAppUserById({ appUserId, signal }),
                    hasRewardModule && VouchersService.listVouchersByAppUserAndCity({ appUserId, cityId, signal }),
                    hasTaxesModule && CityTaxesService.listCityTaxesByAppUserId({ cityId, appUserId, signal }),
                ]);

                setUser(appUserData);
                if (voucherList) {
                    setVoucher(voucherList);
                }

                if (taxesList) {
                    setTaxes(taxesList);
                }
                const invoicesWithNoData = invoicesList.find((i: IMonthData) => i.month === null);

                setTotalProcessingInvoices(invoicesWithNoData?.total_invoices || 0);
                setInvoices(invoicesList.filter((i: IMonthData) => i.month !== null));

                setGiveaways(giveawaysList);

                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                setIsLoading(false);

                EmitError(err, true, 'editAppUser_opasdasjpdasd');

                navigate('/usuarios/aplicativo');
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [id, navigate, activeCity?.id, hasRewardModule, hasTaxesModule]);

    async function handleSubmit(userData: IAppUser) {
        try {

            userData.phone = onlyNumbers(userData.phone);
            await AppUsersService.updateAppUser({ appUserId: Number(id), appUser: userData });

            EmitSuccessAlert('Usuário atualizado com sucesso!');

            navigate('/usuarios/aplicativo');
        } catch (err) {
            EmitError(err, true, 'editAppUser_asiopdasodasd');
        }
    }

    return {
        isLoading,
        user,
        handleSubmit,
        totalProcessingInvoices,
        invoices,
        showModalInvoice,
        handleShowModalInvoice,
        invoicesByMonth,
        loadInvoicesByPeriod,
        loadingInvoices,
        showModalTickets,
        handleShowModalTickets,
        selectedGiveaway,
        filteredGiveaways,
        handleSetSelectedGiveaway,
        filterGiveaways,
        handleSetFilterGiveaway,
        statsUser,
        vouchers,
        loadInvoicesInProcessing,
        invoicesInProcessing,
        loadingInvoicesInProcessing,
        reprocessInvoice,
        loadingInvoicesReprocess,
        handleClickViewInvoicesInProcessing,
        showModalInvoiceInProcessing,
        handleShowModalInvoiceInProcessing,
        taxes,
        setSelectedYear,
        selectedYear,
        availableYears
    }
}
